/**
 * Play / stop logic of the looping audio preview, independent of the DOM so it
 * can be tested with a stub. Only one preview plays at a time.
 */

export interface AudioLike {
  loop: boolean;
  currentTime: number;
  readonly paused: boolean;
  play(): Promise<void> | void;
  pause(): void;
}

const active = new Set<LoopController>();

function pauseOthers(except: LoopController): void {
  for (const other of [...active]) if (other !== except) other.pause();
}

export class LoopController {
  private playing = false;
  private listeners = new Set<(playing: boolean) => void>();

  constructor(private readonly audio: AudioLike) {
    audio.loop = true;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  subscribe(fn: (playing: boolean) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(playing: boolean): void {
    if (this.playing === playing) return;
    this.playing = playing;
    for (const fn of this.listeners) fn(playing);
  }

  /** Starts (looping) after pausing any other preview. */
  async play(): Promise<void> {
    pauseOthers(this);
    this.audio.loop = true;
    active.add(this);
    try {
      await this.audio.play();
      this.set(true);
    } catch {
      active.delete(this);
      this.set(false);
    }
  }

  /** Pauses where it is (used when another preview starts). */
  pause(): void {
    this.audio.pause();
    this.set(false);
  }

  /** Stops and rewinds to the start. */
  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
    active.delete(this);
    this.set(false);
  }

  toggle(): Promise<void> | void {
    return this.playing ? this.pause() : this.play();
  }

  /** Unmount / decision: stop and forget. */
  dispose(): void {
    this.stop();
    this.listeners.clear();
  }
}

/** Formats seconds as m:ss. */
export function clock(seconds: number): string {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
